import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { isAiGalleryImage } from 'utils/campaign';
import { getFileName, getLoadingCropType } from 'utils/clickme';
import { ImageType as BannerImageType, ImageComponent } from 'utils/constants/banner';

import {
    BottomSheet,
    Button,
    createStaticDataProvider,
    Loader,
    Modal,
    NavBar,
    Select,
    StaticDataFetcherItem,
    Text,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24, HyperstarSparkleFilledSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';
import { useShowAiImageGeneration } from 'hooks/useShowAiImageGeneration';

import { Image } from 'components/common/BannerImg/GalleryModal/Image/Image';
import { loadGalleryImages } from 'components/common/BannerImg/GalleryModal/loadGalleryImages';
import { loadImage } from 'components/common/BannerImg/loadImage';
import { UploadField } from 'components/common/BannerImg/UploadField/UploadField';
import withErrorBoundary from 'components/common/ErrorBoundary/withErrorBoundary';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import { setIsAiGenerationModalVisible } from 'store/models/aiImageGeneration';
import {
    setBannerImageComponentByType,
    setBannerImageUpscaleStatusByType,
    setIsCropImageModalVisible,
} from 'store/models/createBanner';
import {
    Image as ImageType,
    setIsAiImageGenerationModalOpenedFromGalleryModal,
    setIsGalleryModalVisible,
} from 'store/models/gallery';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BannerImg/GalleryModal/GalleryModal.module.less';

const TrlKeys = {
    title: 'AdCreate.AdForm.BannerImg.ModalGallery.title',
    description: 'AdCreate.AdForm.BannerImg.ModalGallery.description',
    userLoadedImages: 'AdCreate.AdForm.BannerImg.ModalGallery.userLoadedImages',
    select: {
        placeholder: 'AdCreate.AdForm.BannerImg.ModalGallery.select.search.placeholder',
    },
    generation: 'AdCreate.AdForm.BannerImg.generationMagritte',
};

const GalleryModalComponent: FC = () => {
    const [activeCategory, setActiveCategory] = useState<StaticDataFetcherItem | undefined>(undefined);
    const [images, setImages] = useState<ImageType[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const bannerVacancyId = useSelector((state) => state.createBanner.bannerVacancyId);
    const { userLoadedImages, defaultImages, isLoad, isGalleryModalVisible } = useSelector((state) => state.gallery);
    const { images: bannerImages } = useSelector((state) => state.createBanner);

    const dispatch = useDispatch();
    const trls = useTrls();

    const USER_LOADED = trls(TrlKeys.userLoadedImages);

    const galleryData: { categories: StaticDataFetcherItem[]; initialImages: ImageType[] } = useMemo(() => {
        const profRoles = defaultImages?.map(({ profRoleName }) => profRoleName);
        const categories = (
            userLoadedImages && userLoadedImages.length > 0 ? [USER_LOADED, ...profRoles] : profRoles
        ).map((category) => ({
            value: category,
            text: category,
        }));

        const initialImages = userLoadedImages.length > 0 ? userLoadedImages : defaultImages[0]?.defaultImagesDto;

        return {
            categories,
            initialImages,
        };
    }, [USER_LOADED, userLoadedImages, defaultImages]);

    const { categories, initialImages } = galleryData;

    const dataProvider = useMemo(() => createStaticDataProvider(categories), [categories]);

    const isAiImageGenerationAvailable = useShowAiImageGeneration();
    const showAiGeneration = bannerVacancyId && isAiImageGenerationAvailable;

    useEffect(() => {
        if (categories.length > 0) {
            setActiveCategory(categories[0]);
            setImages(initialImages);
        } else if (isGalleryModalVisible) {
            dispatch(loadGalleryImages());
        }
    }, [categories, dispatch, initialImages, isGalleryModalVisible]);

    const onChange = useCallback(
        (category: StaticDataFetcherItem) => {
            setActiveCategory(category);
            if (category.value === USER_LOADED) {
                setImages(userLoadedImages);
            } else {
                setImages(defaultImages.find((el) => el.profRoleName === category.value)?.defaultImagesDto || []);
            }
        },
        [defaultImages, userLoadedImages, USER_LOADED]
    );

    const chooseImage = (id: number, url: string) => {
        if (isLoading) {
            return;
        }
        const fileName = getFileName(id, url);
        void dispatch(
            loadImage({
                imageType: BannerImageType.Gallery,
                fileName,
                setLoading: setIsLoading,
                id,
                callBack: () => dispatch(setIsCropImageModalVisible(true)),
            })
        );
    };

    const onClose = () => {
        const loadingCropType = getLoadingCropType(bannerImages);
        if (loadingCropType) {
            dispatch([
                setBannerImageComponentByType({
                    cropType: loadingCropType,
                    component: bannerImages[loadingCropType].id ? ImageComponent.Crop : ImageComponent.Input,
                }),
                setBannerImageUpscaleStatusByType({
                    cropType: loadingCropType,
                    upscaleStatus: bannerImages[loadingCropType].upscaleStatus,
                }),
            ]);
        }
        dispatch(setIsGalleryModalVisible(false));
    };

    const options = (
        <>
            <Text style="secondary">{trls(TrlKeys.description)}</Text>
            <VSpacing default={12} />
            <Select
                type="radio"
                name="categories"
                value={activeCategory}
                onChange={onChange}
                dataProvider={dataProvider}
                searchable
                triggerProps={{
                    label: trls(TrlKeys.select.placeholder),
                    size: 'large',
                    stretched: true,
                    'data-qa': 'gallery-modal-select',
                }}
                optionDataQa="gallery-modal-option"
            />
        </>
    );

    const content = (
        <div>
            {isLoad ? (
                <Loader />
            ) : (
                <>
                    {images && images.length > 0 && (
                        <div className={styles.galleryImages}>
                            {images.map(
                                ({
                                    originalImageId,
                                    originalImageWidth,
                                    originalImageHeight,
                                    previewUrl,
                                    cropImageUrl,
                                    createdBy,
                                }) => (
                                    <Image
                                        key={originalImageId}
                                        imageWidth={originalImageWidth}
                                        imageHeight={originalImageHeight}
                                        id={originalImageId}
                                        url={previewUrl}
                                        onClick={() => {
                                            chooseImage(originalImageId, cropImageUrl);
                                        }}
                                        userLoadedImages={activeCategory?.value === USER_LOADED}
                                        isAi={isAiGalleryImage(createdBy)}
                                    />
                                )
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );

    const footer = (
        <div className={styles.footer}>
            {showAiGeneration && (
                <Button
                    mode="secondary"
                    style="neutral"
                    onClick={() => {
                        dispatch([
                            setIsAiGenerationModalVisible(true),
                            setIsAiImageGenerationModalOpenedFromGalleryModal(true),
                        ]);
                        onClose();
                    }}
                    icon={<HyperstarSparkleFilledSize24 initialColor="negative" />}
                    stretched
                    spaceBetween={false}
                >
                    {trls(TrlKeys.generation)}
                </Button>
            )}
            <VSpacing default={20} />
            <UploadField onlyField />
        </div>
    );

    return (
        <>
            <Modal
                visible={isGalleryModalVisible}
                onClose={onClose}
                size="large"
                header={
                    <MagritteModalHeader
                        title={trls(TrlKeys.title)}
                        actions={<CrossOutlinedSize24 initialColor="secondary" onClick={onClose} />}
                        options={options}
                    />
                }
                footer={footer}
            >
                {content}
            </Modal>
            <BottomSheet
                visible={isGalleryModalVisible}
                onClose={onClose}
                height="full-screen"
                footer={footer}
                header={
                    <NavBar showDivider="always">
                        <NavBar.Pane>
                            <NavBar.Stage>
                                <NavBar.Actions left centered right={<CrossOutlinedSize24 onClick={onClose} />}>
                                    <NavBar.TitleContainer
                                        title={<Text typography="subtitle-1-semibold">{trls(TrlKeys.title)}</Text>}
                                    />
                                </NavBar.Actions>
                                <VSpacing default={16} />
                                {options}
                            </NavBar.Stage>
                        </NavBar.Pane>
                    </NavBar>
                }
            >
                {content}
            </BottomSheet>
        </>
    );
};

const GalleryModal = withErrorBoundary(GalleryModalComponent);
export { GalleryModal };
